'use client'

import React, { useMemo } from 'react'
import { TranslationsProvider } from '@eo-locale/react'

import {
  constants,
  getTwoLetterISOLanguageName,
  UserSettings,
  RequestServerContext,
  webToggles as defaultToggles,
  MicrositeSettings,
} from '@dustin-web/microsite-config'
import { WebTogglesWithOverrides } from '@dustin-web/microsite-config/src/web-toggles'

import { AuthProvider } from '@dustin-web/microsite-components/src/auth'
import { QueryStateProvider, LayoutProvider } from '@dustin-web/microsite-components/src/hooks'
import { WebTogglesContext } from '@dustin-web/microsite-components/src/hooks/use-web-toggles'
import { UserSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-user-settings'
import { SiteContextProvider } from '@dustin-web/microsite-components/src/hooks/use-site-context'
import { MicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { CookiesContext } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { SWRConfig } from 'swr'
import { PortalProvider } from '@dustin-web/microsite-components/src/hooks/portal'
import {
  OptimizelyProvider,
  getOrCreateOptimizelyClient,
} from '@dustin-web/microsite-components/src/hooks/use-optimizely'
import {
  UseShoppingCart,
  useShoppingCart,
} from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import {
  UseComparisons,
  useComparisons,
} from '@dustin-web/microsite-data/src/hooks/use-comparisons-internal'
import {
  UseCategoryNavigation,
  useCategoryNavigation,
} from '@dustin-web/microsite-data/src/hooks/use-category-navigation-internal'
import {
  UseFavorites,
  useFavorites,
} from '@dustin-web/microsite-data/src/hooks/use-favorites-internal'
import { DataLoaderHooksProvider } from '@dustin-web/product-components/src/hooks/dataloader/use-dataloader-hooks'
import { UseAccount, useAccount } from '@dustin-web/microsite-data/src/hooks/use-account-internal'
import {
  useNotifyCart,
  type UseNotifyAddToCart,
} from '@dustin-web/microsite-data/src/hooks/use-notify-cart-internal'
import useNotifyClick, {
  type UseNotifyClick,
} from '@dustin-web/microsite-data/src/hooks/use-notify-click-internal'
import { AssistiveCartProvider } from '@dustin-web/microsite-components/src/hooks/use-assistive-cart-context'
import OktaProvider from '@dustin-web/microsite-components/src/okta-auth/okta-provider'
import { FeatureFlagsProvider } from "@dustin-web/microsite-data/src/feature-flags/client";

type DataLoaderHooks = {
  favorites: UseFavorites
  shoppingcart: UseShoppingCart
  compare: UseComparisons
  account: UseAccount
  categoryNavigation: UseCategoryNavigation
  notifyAddToCart: UseNotifyAddToCart
  notifyClick: UseNotifyClick
}

let hasIntlErrors = false
const onIntlError = (intlError: any) => {
  if (!hasIntlErrors) {
    if (intlError.message.includes('[eo-locale] id missing')) {
      // eslint-disable-next-line no-console
      console.warn(intlError.message, 'Further intl errors will be suppressed.')
      hasIntlErrors = true

      return
    }

    throw intlError
  }
}

type SwrRootProps = {
  fallback: any
  requestContext: RequestServerContext
  userSettings: UserSettings
  micrositeSettings: MicrositeSettings
  cookies: { [key: string]: string }
  webToggles: WebTogglesWithOverrides
  messages: { [key: string]: string }
  query?: { [key: string]: string }
  headers?: NodeJS.Dict<string | string[]>
  optimizelyDatafile?: string
  initialAuthenticated?: boolean
  children: React.ReactNode
}

const Root = ({
  fallback = {},
  requestContext,
  userSettings,
  micrositeSettings,
  cookies,
  webToggles = { ...defaultToggles, overrides: {} } as WebTogglesWithOverrides,
  messages = {},
  query = {},
  optimizelyDatafile,
  initialAuthenticated,
  children,
}: SwrRootProps) => {
  const twoLetterISOLanguageName = getTwoLetterISOLanguageName(userSettings?.language)

  const hasAnalyticsConsent = useMemo(
    () => {
      const analyticsCookieKey = constants?.cookies?.consent?.analytics

      if (!cookies || !analyticsCookieKey) {
        return false
      }

      return cookies[analyticsCookieKey] === 'true'
    },
    [cookies]
  )

  const optimizely = useMemo(
    () => getOrCreateOptimizelyClient(optimizelyDatafile, hasAnalyticsConsent),
    [optimizelyDatafile, hasAnalyticsConsent]
  )

  // We are only loading one language so no on-the-fly switching of locales allowed
  const locales = [
    {
      language: twoLetterISOLanguageName,
      messages,
    },
  ]

  const dataLoaderHooks: DataLoaderHooks = {
    favorites: useFavorites,
    shoppingcart: useShoppingCart,
    compare: useComparisons,
    account: useAccount,
    categoryNavigation: useCategoryNavigation,
    notifyAddToCart: useNotifyCart,
    notifyClick: useNotifyClick,
  }

  return (
    <MicrositeSettingsContext.Provider value={micrositeSettings}>
      <SiteContextProvider value={requestContext}>
        <CookiesContext.Provider value={cookies}>
          <UserSettingsContext.Provider value={userSettings}>
            <AuthProvider config={micrositeSettings.authConfig} initialAuthenticated={initialAuthenticated}>
              <OktaProvider>
                <OptimizelyProvider
                  optimizely={optimizely}
                  hasAnalyticsConsent={hasAnalyticsConsent}>
                  <FeatureFlagsProvider datafile={optimizelyDatafile}>
                    <WebTogglesContext.Provider value={webToggles}>
                      <QueryStateProvider query={query}>
                        <SWRConfig value={{ fallback }}>
                          <TranslationsProvider
                            onError={onIntlError}
                            language={twoLetterISOLanguageName}
                            locales={locales}>
                            <LayoutProvider>
                              <PortalProvider>
                                <DataLoaderHooksProvider dataLoaderHooks={dataLoaderHooks}>
                                  <AssistiveCartProvider>
                                    {children}
                                  </AssistiveCartProvider>
                                </DataLoaderHooksProvider>
                              </PortalProvider>
                            </LayoutProvider>
                          </TranslationsProvider>
                        </SWRConfig>
                      </QueryStateProvider>
                    </WebTogglesContext.Provider>
                  </FeatureFlagsProvider>
                </OptimizelyProvider>
              </OktaProvider>
            </AuthProvider>
          </UserSettingsContext.Provider>
        </CookiesContext.Provider>
      </SiteContextProvider>
    </MicrositeSettingsContext.Provider>
  )
}

export default Root
